import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { invokeSendPush } from '../lib/push/sendPushNotification';
import { Cliente } from '../types';
import { normalizeClientePhone } from '../lib/phoneUtils';
import { fetchByEmpresaId, updateById, deleteById, createForEmpresa } from '../utils/supabaseHelpers';
import { handleStoreAction } from '../utils/storeHelpers';
import { logger } from '../utils/logger';
import { useAuthStore } from './useAuthStore';

/** Coalesce concurrent fetchClientes con la misma empresa y modo (lista completa vs página). */
const fetchClientesInFlight = new Map<string, Promise<Cliente[] | null>>();

// Helper function to convert Supabase cliente to local format
const convertSupabaseToLocal = (c: any): Cliente => ({
  id: c.id,
  nombre: c.nombre,
  apellido: c.apellido || '',
  telefono: c.telefono || '',
  email: c.email || '',
  direccion: c.direccion || '',
  ciudad: c.ciudad || '',
  tipoDocumento: c.tipo_documento || 'CC',
  numeroDocumento: c.numero_documento || '',
  razonSocial: c.razon_social || undefined,
  tipoCliente:
    c.tipo_cliente === 'EMPRESA' || c.es_empresa === true
      ? 'EMPRESA'
      : c.tipo_cliente === 'INDIVIDUAL'
        ? 'INDIVIDUAL'
        : undefined,
  fechaNacimiento: c.fecha_nacimiento ? new Date(c.fecha_nacimiento) : undefined,
  whatsapp_number: c.whatsapp_number || undefined,
  activo: c.activo !== false,
  createdAt: new Date(c.created_at || new Date()),
  updatedAt: new Date(c.updated_at || new Date()),
  empresaId: c.empresa_id || '',
  // Additional fields with defaults (use DB values when present)
  barrio: c.barrio || '',
  ultimaCompra: c.ultima_compra ? new Date(c.ultima_compra) : undefined,
  totalCompras: c.total_compras || 0,
  rentabilidadTotal: c.rentabilidad_total || 0,
  promedioCompra: c.promedio_compra || 0,
  frecuenciaCompra: c.frecuencia_compra || 0,
  riesgoChurn: c.riesgo_churn || 'BAJO',
  segmento: c.segmento || 'REGULAR',
  metodoPagoPreferido: 'EFECTIVO',
  canalPreferido: 'PRESENCIAL',
  notas: c.notas || '',
  // CRM Pipeline fields
  pipeline_stage: c.pipeline_stage || undefined,
  assigned_user_id: c.assigned_user_id || undefined,
  last_contacted: c.last_contacted ? new Date(c.last_contacted) : undefined,
  next_followup: c.next_followup ? new Date(c.next_followup) : undefined,
  valor_estimado: c.valor_estimado || 0,
  valor_cerrado: c.valor_cerrado || 0,
  fecha_cierre: c.fecha_cierre ? new Date(c.fecha_cierre) : undefined,
  source: c.source || 'manual'
});

// Helper function to convert local cliente to Supabase format
// For updates, only include fields that are actually being updated (don't include id, created_at)
const convertLocalToSupabase = (
  c: Partial<Cliente>,
  empresaId: string,
  isUpdate: boolean = false,
  empresaPais: string = 'CO',
) => {
  const base: any = {
    empresa_id: empresaId,
    updated_at: new Date().toISOString(),
  };

  // Only include id and created_at for new records (creates)
  if (!isUpdate) {
    base.id = c.id || crypto.randomUUID();
    base.created_at = c.createdAt ? new Date(c.createdAt).toISOString() : new Date().toISOString();
  }

  // Only include fields that are actually present in the partial Cliente object
  if (c.nombre !== undefined) base.nombre = c.nombre || '';
  if (c.apellido !== undefined) base.apellido = c.apellido || '';
  if (c.telefono !== undefined) {
    const raw = c.telefono || '';
    base.telefono = raw ? normalizeClientePhone(String(raw), empresaPais) : '';
  }
  if (c.email !== undefined) base.email = c.email || '';
  if (c.direccion !== undefined) base.direccion = c.direccion || '';
  if (c.ciudad !== undefined) base.ciudad = c.ciudad || '';
  if (c.tipoDocumento !== undefined) base.tipo_documento = c.tipoDocumento || 'CC';
  if (c.numeroDocumento !== undefined) base.numero_documento = c.numeroDocumento || '';
  if (c.activo !== undefined) base.activo = c.activo !== false;
  if (c.segmento !== undefined) base.segmento = c.segmento || null;
  if (c.riesgoChurn !== undefined) base.riesgo_churn = c.riesgoChurn || null;
  if (c.barrio !== undefined) base.barrio = c.barrio || '';
  if (c.razonSocial !== undefined) base.razon_social = c.razonSocial || null;
  if (c.tipoCliente !== undefined) {
    base.tipo_cliente = c.tipoCliente;
    base.es_empresa = c.tipoCliente === 'EMPRESA';
  }
  if (c.fechaNacimiento !== undefined) {
    if (!c.fechaNacimiento) {
      base.fecha_nacimiento = null;
    } else {
      const d = c.fechaNacimiento instanceof Date ? c.fechaNacimiento : new Date(c.fechaNacimiento);
      base.fecha_nacimiento = Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
    }
  }
  if (c.notas !== undefined) base.notas = c.notas || '';

  // CRM Pipeline fields - only include if present
  // Handle pipeline_stage: explicitly set to null when undefined to remove from pipeline
  // When pipeline_stage is explicitly undefined, we want to set it to null in DB to remove from pipeline
  if (c.pipeline_stage !== undefined) {
    base.pipeline_stage = c.pipeline_stage || null;
  } else if (c.hasOwnProperty('pipeline_stage')) {
    // If pipeline_stage is explicitly set to undefined (property exists but value is undefined), set to null
    base.pipeline_stage = null;
  }
  if (c.assigned_user_id !== undefined) base.assigned_user_id = c.assigned_user_id || null;
  if (c.last_contacted !== undefined) base.last_contacted = c.last_contacted ? new Date(c.last_contacted).toISOString() : null;
  if (c.next_followup !== undefined) base.next_followup = c.next_followup ? new Date(c.next_followup).toISOString() : null;
  if (c.valor_estimado !== undefined) base.valor_estimado = c.valor_estimado || 0;
  if (c.valor_cerrado !== undefined) base.valor_cerrado = c.valor_cerrado || 0;
  if (c.fecha_cierre !== undefined) base.fecha_cierre = c.fecha_cierre ? new Date(c.fecha_cierre).toISOString() : null;
  if (c.source !== undefined) base.source = c.source || 'manual';

  return base;
};

const CLIENTES_PAGE_SIZE = 50;
const VENTAS_CACHE_LIMIT = 100_000;
const FACTURAS_PENDIENTES_CACHE_LIMIT = 100_000;

/** Factura no archivada/eliminada y con pago pendiente (alineado con filtro en GestionFacturas). */
function isFacturaPendienteRow(row: { estado?: string | null; estado_pago?: string | null }): boolean {
  const est = row.estado || '';
  if (est === 'ARCHIVADA' || est === 'ELIMINADA') return false;
  const ep = row.estado_pago || '';
  return ep !== 'PAGADO' && ep !== 'CANCELADO';
}

function aggregateFacturasPendientesPorCliente(
  rows: { cliente_id?: string | null; estado?: string | null; estado_pago?: string | null }[] | null
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!rows) return out;
  for (const row of rows) {
    const cid = row.cliente_id;
    if (!cid || !isFacturaPendienteRow(row)) continue;
    out[cid] = (out[cid] || 0) + 1;
  }
  return out;
}

interface ClientesState {
  clientes: Cliente[];
  isLoading: boolean;
  error: string | null;
  /** Total count when using pagination (exact from DB) */
  clientesTotalCount: number | null;
  /** Next offset for "load more" (current length of loaded clientes when paginated) */
  clientesOffset: number;
  /** Cached ventas aggregated by cliente_id per empresa (avoids re-fetch on "load more") */
  ventasCacheByEmpresaId: Record<string, Record<string, { fecha?: string; total?: number; rentabilidad_real?: number }[]>>;
  /** Conteo de facturas con pago pendiente por cliente_id, por empresa */
  facturasPendientesCacheByEmpresaId: Record<string, Record<string, number>>;

  _statsFromVentas: (ventas: { fecha?: string; total?: number; rentabilidad_real?: number }[]) => {
    totalCompras: number;
    ultimaCompra: Date | null;
    rentabilidadTotal: number;
    promedioCompra: number;
    frecuenciaCompra: number;
    lifetimeSales: number;
    last6MonthsSales: number;
    lastPurchaseAmount: number;
  };

  // Actions
  fetchClientes: (empresaId: string, options?: { limit?: number; offset?: number }) => Promise<Cliente[] | null>;
  fetchMoreClientes: (empresaId: string) => Promise<void>;
  createCliente: (cliente: Omit<Cliente, 'id' | 'createdAt' | 'updatedAt'>, empresaId: string) => Promise<Cliente | null>;
  updateCliente: (id: string, updates: Partial<Cliente>, empresaId: string) => Promise<Cliente | null>;
  deleteCliente: (id: string) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
  calcularEstadisticasCliente: (clienteId: string) => Promise<any>;
  actualizarEstadisticasCliente: (clienteId: string) => Promise<void>;
}

// No mock data - all data comes from Supabase

export const useClientesStore = create<ClientesState>((set, get) => ({
  clientes: [],
  isLoading: false,
  error: null,
  clientesTotalCount: null,
  clientesOffset: 0,
  ventasCacheByEmpresaId: {},
  facturasPendientesCacheByEmpresaId: {},

  /** Compute stats from a list of ventas (same shape as Supabase response: fecha, total, rentabilidad_real) */
  _statsFromVentas: (ventas: { fecha?: string; total?: number; rentabilidad_real?: number }[]) => {
    if (!ventas || ventas.length === 0) {
      return {
        totalCompras: 0,
        ultimaCompra: null as Date | null,
        rentabilidadTotal: 0,
        promedioCompra: 0,
        frecuenciaCompra: 0,
        lifetimeSales: 0,
        last6MonthsSales: 0,
        lastPurchaseAmount: 0
      };
    }
    const totalCompras = ventas.length;
    const ultimaCompra = ventas[0]?.fecha ? new Date(ventas[0].fecha) : null;
    const rentabilidadTotal = ventas.reduce((sum, v) => sum + (v.rentabilidad_real || 0), 0);
    const promedioCompra = ventas.reduce((sum, v) => sum + (v.total || 0), 0) / totalCompras;
    const lifetimeSales = ventas.reduce((sum, v) => sum + (v.total || 0), 0);
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const ventasUltimos6Meses = ventas.filter(v => {
      const fechaVenta = new Date(v.fecha || 0);
      return fechaVenta >= sixMonthsAgo;
    });
    const last6MonthsSales = ventasUltimos6Meses.reduce((sum, v) => sum + (v.total || 0), 0);
    const lastPurchaseAmount = ultimaCompra ? (ventas[0]?.total || 0) : 0;
    const lastVenta = ventas[ventas.length - 1];
    const fechaPrimeraCompra = lastVenta?.fecha ? new Date(lastVenta.fecha) : null;
    const mesesTranscurridos = ultimaCompra && fechaPrimeraCompra
      ? Math.max(1, Math.ceil((ultimaCompra.getTime() - fechaPrimeraCompra.getTime()) / (1000 * 60 * 60 * 24 * 30)))
      : 1;
    const frecuenciaCompra = totalCompras / mesesTranscurridos;
    return {
      totalCompras,
      ultimaCompra,
      rentabilidadTotal,
      promedioCompra,
      frecuenciaCompra,
      lifetimeSales,
      last6MonthsSales,
      lastPurchaseAmount
    };
  },

  // Calculate client statistics from actual ventas (single-client; used by actualizarEstadisticasCliente)
  calcularEstadisticasCliente: async (clienteId: string) => {
    try {
      logger.debug('📊 Calculating statistics for cliente:', clienteId);
      const { data: ventas, error } = await supabase
        .from('ventas')
        .select('fecha, total, rentabilidad_real')
        .eq('cliente_id', clienteId)
        .order('fecha', { ascending: false });

      if (error) {
        logger.error('❌ Error fetching ventas for cliente:', error);
        return null;
      }
      return get()._statsFromVentas(ventas || []);
    } catch (error) {
      logger.error('❌ Error calculating cliente statistics:', error);
      return null;
    }
  },

  // Update client statistics
  actualizarEstadisticasCliente: async (clienteId: string) => {
    const estadisticas = await get().calcularEstadisticasCliente(clienteId);
    if (!estadisticas) return;

    // Update the client in the store
    set(state => ({
      clientes: state.clientes.map(cliente =>
        cliente.id === clienteId
          ? {
              ...cliente,
              totalCompras: estadisticas.totalCompras,
              ultimaCompra: estadisticas.ultimaCompra,
              rentabilidadTotal: estadisticas.rentabilidadTotal,
              promedioCompra: estadisticas.promedioCompra,
              frecuenciaCompra: estadisticas.frecuenciaCompra,
              lifetimeSales: estadisticas.lifetimeSales,
              last6MonthsSales: estadisticas.last6MonthsSales,
              lastPurchaseAmount: estadisticas.lastPurchaseAmount,
              facturasPendientesCount: cliente.facturasPendientesCount
            }
          : cliente
      )
    }));
  },

  fetchClientes: async (empresaId: string, options?: { limit?: number; offset?: number }) => {
    const usePagination = options?.limit != null && options.limit > 0;
    const limit = options?.limit ?? 0;
    const offset = options?.offset ?? 0;
    const inflightKey = usePagination ? `${empresaId}:p:${limit}:${offset}` : `${empresaId}:full`;

    const existing = fetchClientesInFlight.get(inflightKey);
    if (existing) return existing;

    const run = (async (): Promise<Cliente[] | null> => {
      try {
        return await handleStoreAction(
          async () => {
        logger.debug('🔍 Fetching clientes for empresa:', empresaId, options);
        
        if (!empresaId) {
          logger.warn('❌ No empresa ID provided');
          throw new Error('Para gestionar clientes, primero necesitas crear una empresa. Ve al dashboard y haz clic en "Nueva Empresa".');
        }

        // 1) Fetch clientes (one query — with range when paginating)
        let data: any[] | null;
        let totalCount: number | null = null;

        if (usePagination) {
          const from = offset;
          const to = offset + limit - 1;
          const { data: pageData, error, count } = await supabase
            .from('clientes')
            .select('*', { count: 'exact' })
            .eq('empresa_id', empresaId)
            .order('created_at', { ascending: false })
            .range(from, to);
          if (error) throw error;
          data = pageData;
          totalCount = count ?? null;
        } else {
          const result = await fetchByEmpresaId<any>('clientes', empresaId);
          if (result.error) throw result.error;
          data = result.data;
        }

        logger.debug('👥 Clientes cargados desde Supabase:', data?.length || 0, usePagination ? `(total: ${totalCount})` : '');
        
        const clientesLocales = (data || []).map(convertSupabaseToLocal);

        // 2) Ventas: one query per empresa, cached (so "load more" does not re-fetch)
        let ventasByClienteId = get().ventasCacheByEmpresaId[empresaId];
        if (!ventasByClienteId) {
          try {
            const { data: ventasData, error: ventasError } = await supabase
              .from('ventas')
              .select('cliente_id, fecha, total, rentabilidad_real')
              .eq('empresa_id', empresaId)
              .order('fecha', { ascending: false })
              .limit(VENTAS_CACHE_LIMIT);

            if (!ventasError && ventasData && ventasData.length > 0) {
              ventasByClienteId = (ventasData as { cliente_id: string; fecha?: string; total?: number; rentabilidad_real?: number }[]).reduce(
                (acc, v) => {
                  const cid = v.cliente_id;
                  const arr = acc[cid] ?? [];
                  arr.push({ fecha: v.fecha, total: v.total, rentabilidad_real: v.rentabilidad_real });
                  acc[cid] = arr;
                  return acc;
                },
                {} as Record<string, { fecha?: string; total?: number; rentabilidad_real?: number }[]>
              );
              set({
                ventasCacheByEmpresaId: {
                  ...get().ventasCacheByEmpresaId,
                  [empresaId]: ventasByClienteId
                }
              });
            } else {
              ventasByClienteId = {};
            }
          } catch (e) {
            logger.warn('⚠️ Could not fetch ventas for client stats (clientes will still load):', e);
            ventasByClienteId = {};
          }
        }

        let pendingByClienteId = get().facturasPendientesCacheByEmpresaId[empresaId];
        if (!pendingByClienteId) {
          try {
            const { data: facturasRows, error: facturasError } = await supabase
              .from('facturas')
              .select('cliente_id, estado, estado_pago')
              .eq('empresa_id', empresaId)
              .limit(FACTURAS_PENDIENTES_CACHE_LIMIT);

            if (!facturasError && facturasRows) {
              pendingByClienteId = aggregateFacturasPendientesPorCliente(facturasRows);
              set({
                facturasPendientesCacheByEmpresaId: {
                  ...get().facturasPendientesCacheByEmpresaId,
                  [empresaId]: pendingByClienteId
                }
              });
            } else {
              pendingByClienteId = {};
              if (facturasError) {
                logger.warn('⚠️ Could not fetch facturas pendientes for client list:', facturasError);
              }
            }
          } catch (e) {
            logger.warn('⚠️ Could not aggregate facturas pendientes (clientes will still load):', e);
            pendingByClienteId = {};
          }
        }

        const pendingCounts = pendingByClienteId ?? {};

        const statsFromVentas = get()._statsFromVentas;
        const clientesConStats = clientesLocales.map((cliente) => {
          const ventas = ventasByClienteId?.[cliente.id] || [];
          const estadisticas = statsFromVentas(ventas);
          return {
            ...cliente,
            totalCompras: cliente.totalCompras != null ? cliente.totalCompras : estadisticas.totalCompras,
            ultimaCompra: cliente.ultimaCompra ?? estadisticas.ultimaCompra ?? undefined,
            rentabilidadTotal: cliente.rentabilidadTotal != null ? cliente.rentabilidadTotal : estadisticas.rentabilidadTotal,
            promedioCompra: cliente.promedioCompra != null ? cliente.promedioCompra : estadisticas.promedioCompra,
            frecuenciaCompra: estadisticas.frecuenciaCompra,
            lifetimeSales: estadisticas.lifetimeSales,
            last6MonthsSales: estadisticas.last6MonthsSales,
            lastPurchaseAmount: estadisticas.lastPurchaseAmount,
            facturasPendientesCount: pendingCounts[cliente.id] ?? 0
          };
        });

        set({
          clientes: clientesConStats,
          clientesTotalCount: usePagination ? totalCount : clientesConStats.length,
          clientesOffset: usePagination ? offset + clientesConStats.length : 0,
        });
        return clientesConStats;
          },
          (error) => set({ error }),
          (loading) => set({ isLoading: loading }),
          { errorPrefix: 'Error al cargar clientes' }
        );
      } finally {
        fetchClientesInFlight.delete(inflightKey);
      }
    })();

    fetchClientesInFlight.set(inflightKey, run);
    return run;
  },

  fetchMoreClientes: async (empresaId: string) => {
    const state = get();
    const total = state.clientesTotalCount;
    const offset = state.clientesOffset;
    const pageSize = CLIENTES_PAGE_SIZE;
    if (total == null || offset >= total) return;
    set({ isLoading: true });
    try {
      const from = offset;
      const to = offset + pageSize - 1;
      const { data: pageData, error } = await supabase
        .from('clientes')
        .select('*')
        .eq('empresa_id', empresaId)
        .order('created_at', { ascending: false })
        .range(from, to);
      if (error) throw error;
      const clientesLocales = (pageData || []).map(convertSupabaseToLocal);
      const ventasByClienteId = state.ventasCacheByEmpresaId[empresaId] || {};
      const pendingByClienteId = state.facturasPendientesCacheByEmpresaId[empresaId] || {};
      const statsFromVentas = get()._statsFromVentas;
      const clientesConStats = clientesLocales.map((cliente) => {
        const ventas = ventasByClienteId[cliente.id] || [];
        const estadisticas = statsFromVentas(ventas);
        return {
          ...cliente,
          totalCompras: cliente.totalCompras != null ? cliente.totalCompras : estadisticas.totalCompras,
          ultimaCompra: cliente.ultimaCompra ?? estadisticas.ultimaCompra ?? undefined,
          rentabilidadTotal: cliente.rentabilidadTotal != null ? cliente.rentabilidadTotal : estadisticas.rentabilidadTotal,
          promedioCompra: cliente.promedioCompra != null ? cliente.promedioCompra : estadisticas.promedioCompra,
          frecuenciaCompra: estadisticas.frecuenciaCompra,
          lifetimeSales: estadisticas.lifetimeSales,
          last6MonthsSales: estadisticas.last6MonthsSales,
          lastPurchaseAmount: estadisticas.lastPurchaseAmount,
          facturasPendientesCount: pendingByClienteId[cliente.id] ?? 0
        };
      });
      set({
        clientes: [...state.clientes, ...clientesConStats],
        clientesOffset: offset + clientesConStats.length,
        isLoading: false
      });
    } catch (e) {
      logger.warn('⚠️ fetchMoreClientes failed:', e);
      set({ isLoading: false });
    }
  },

  createCliente: async (cliente: Omit<Cliente, 'id' | 'createdAt' | 'updatedAt'>, empresaId: string) => {
    return handleStoreAction(
      async () => {
        logger.debug('🔄 Creating cliente:', cliente);
        
        if (!empresaId) {
          throw new Error('Para crear clientes, primero necesitas crear una empresa. Ve al dashboard y haz clic en "Nueva Empresa".');
        }
        
        const empresaPais = useAuthStore.getState().currentEmpresa?.pais ?? 'CO';
        // Convert to Supabase format
        const supabaseCliente = convertLocalToSupabase(cliente, empresaId, false, empresaPais);
        
        logger.debug('🔍 CLIENTE TO CREATE:', supabaseCliente);
        
        const { data: created, error } = await createForEmpresa<any>('clientes', empresaId, supabaseCliente);
        
        if (error) throw error;
        if (!created) throw new Error('Failed to create cliente');
        
        logger.debug('✅ Cliente creado en Supabase:', created);
        
        // Convert back to local format
        const nuevoCliente = convertSupabaseToLocal(created);
        const pendingMap = get().facturasPendientesCacheByEmpresaId[empresaId] || {};
        const nuevoConPendientes = { ...nuevoCliente, facturasPendientesCount: pendingMap[nuevoCliente.id] ?? 0 };

        // Add to local state
        const updatedClientes = [...get().clientes, nuevoConPendientes];
        set({ clientes: updatedClientes });

        return nuevoConPendientes;
      },
      (error) => set({ error }),
      (loading) => set({ isLoading: loading }),
      {
        errorPrefix: 'Error al crear cliente'
      }
    );
  },

  updateCliente: async (id: string, updates: Partial<Cliente>, empresaId: string) => {
    return handleStoreAction(
      async () => {
        logger.debug('🔄 Updating cliente:', id, updates);
        
        if (!empresaId) {
          throw new Error('Usuario no tiene empresa asignada');
        }
        
        // Convert to Supabase format
        const empresaPais = useAuthStore.getState().currentEmpresa?.pais ?? 'CO';
        // Convert to Supabase format (isUpdate = true to exclude id, created_at, and only include fields being updated)
        const supabaseUpdates = convertLocalToSupabase(updates, empresaId, true, empresaPais);
        
        logger.debug('🔍 CLIENTE UPDATES:', supabaseUpdates);
        logger.debug('🔍 Pipeline stage update:', {
          originalValue: updates.pipeline_stage,
          originalType: typeof updates.pipeline_stage,
          supabaseValue: supabaseUpdates.pipeline_stage,
          supabaseType: typeof supabaseUpdates.pipeline_stage
        });
        
        const { data: updated, error } = await updateById<any>('clientes', id, supabaseUpdates);
        
        if (error) throw error;
        if (!updated) throw new Error('Failed to update cliente');
        
        logger.debug('✅ Cliente actualizado en Supabase:', {
          id: updated.id,
          pipeline_stage: updated.pipeline_stage,
          pipeline_stageType: typeof updated.pipeline_stage
        });
        
        // Convert back to local format
        const clienteLocal = convertSupabaseToLocal(updated);
        const pendingMap = get().facturasPendientesCacheByEmpresaId[empresaId] || {};
        const prev = get().clientes.find(c => c.id === id);
        const clienteLocalConPendientes = {
          ...clienteLocal,
          facturasPendientesCount: prev?.facturasPendientesCount ?? pendingMap[id] ?? 0
        };

        logger.debug('🔍 Cliente convertido a formato local:', {
          id: clienteLocalConPendientes.id,
          pipeline_stage: clienteLocalConPendientes.pipeline_stage,
          pipeline_stageType: typeof clienteLocalConPendientes.pipeline_stage
        });

        // Update local state
        const currentClientes = get().clientes;
        const updatedClientes = currentClientes.map(c =>
          c.id === id ? clienteLocalConPendientes : c
        );
        
        logger.debug('🔍 Estado antes de actualizar:', {
          total: currentClientes.length,
          clienteAntes: currentClientes.find(c => c.id === id)?.pipeline_stage
        });
        
        set({ clientes: updatedClientes });
        
        logger.debug('🔍 Estado después de actualizar:', {
          total: updatedClientes.length,
          clienteDespues: updatedClientes.find(c => c.id === id)?.pipeline_stage
        });

        const uuidRe =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        const assigneeRaw =
          clienteLocalConPendientes.assigned_user_id != null
            ? String(clienteLocalConPendientes.assigned_user_id).trim()
            : '';
        if (prev && empresaId) {
          const segPrev = String(prev.segmento ?? '');
          const segNew = String(clienteLocalConPendientes.segmento ?? '');
          if (segPrev !== segNew && assigneeRaw && uuidRe.test(assigneeRaw)) {
            invokeSendPush({
              empresa_id: empresaId,
              event_type: 'vip_change',
              title: 'Cambio de segmento',
              body: `Cliente: ${segPrev || '—'} → ${segNew || '—'}`,
              tipo: 'segmento_cliente',
              usuario_id: assigneeRaw,
              data: { deep_link: 'clientelo://Clientes', cliente_id: id },
            }).then(() => {});
          }
          const churnPrev = String(prev.riesgoChurn ?? '');
          const churnNew = String(clienteLocalConPendientes.riesgoChurn ?? '');
          if (churnPrev !== churnNew && churnNew.toUpperCase() === 'ALTO') {
            invokeSendPush({
              empresa_id: empresaId,
              event_type: 'churn_risk',
              title: 'Riesgo de fuga',
              body: `Un cliente pasó a riesgo ALTO (${clienteLocalConPendientes.nombre || ''} ${clienteLocalConPendientes.apellido || ''}).`,
              tipo: 'riesgo_churn',
              notify_empresa_admins: true,
              data: { deep_link: 'clientelo://Clientes', cliente_id: id },
            }).then(() => {});
          }
        }
        
        return clienteLocalConPendientes;
      },
      (error) => set({ error }),
      (loading) => set({ isLoading: loading }),
      {
        errorPrefix: 'Error al actualizar cliente'
      }
    );
  },

  deleteCliente: async (id: string) => {
    const result = await handleStoreAction(
      async () => {
        logger.debug('🔄 Deleting cliente:', id);
        
        // First, delete related records to avoid foreign key constraint violations
        // Order matters: delete child records first, then parent
        
        // 1. Delete appointments referencing this cliente
        try {
          const { error: appointmentsError } = await supabase
            .from('appointments')
            .delete()
            .eq('cliente_id', id);
          
          if (appointmentsError && appointmentsError.code !== 'PGRST116') {
            // PGRST116 = no rows found, which is fine
            logger.warn('⚠️ Error deleting appointments:', appointmentsError);
          }
        } catch (err) {
          // Table might not exist, continue
        }
        
        // 2. Delete webform_submissions referencing this cliente
        try {
          const { error: webformError } = await supabase
            .from('webform_submissions')
            .delete()
            .eq('cliente_id', id);
          
          if (webformError && webformError.code !== 'PGRST116') {
            // PGRST116 = no rows found, which is fine
            // Log but don't throw - RLS might prevent deletion, but try to continue
            logger.warn('⚠️ Error deleting webform_submissions:', webformError);
            // If RLS is blocking, the foreign key constraint will still fail
            // In that case, we'll catch it in the final delete attempt
          }
        } catch (err) {
          // Table might not exist, continue
        }
        
        // 3. Delete cliente_notes referencing this cliente
        try {
          const { error: notesError } = await supabase
            .from('cliente_notes')
            .delete()
            .eq('cliente_id', id);
          
          if (notesError && notesError.code !== 'PGRST116') {
            logger.warn('⚠️ Error deleting cliente_notes:', notesError);
          }
        } catch (err) {
          // Continue if table doesn't exist
        }
        
        // 4. Delete ventas referencing this cliente (if CASCADE not set)
        try {
          const { error: ventasError } = await supabase
            .from('ventas')
            .delete()
            .eq('cliente_id', id);
          
          if (ventasError && ventasError.code !== 'PGRST116') {
            logger.warn('⚠️ Error deleting ventas:', ventasError);
            // Don't throw - might be CASCADE set, so deletion will work anyway
          }
        } catch (err) {
          // Continue
        }
        
        // 5. Delete facturas referencing this cliente (if CASCADE not set)
        try {
          const { error: facturasError } = await supabase
            .from('facturas')
            .delete()
            .eq('cliente_id', id);
          
          if (facturasError && facturasError.code !== 'PGRST116') {
            logger.warn('⚠️ Error deleting facturas:', facturasError);
            // Don't throw - might be CASCADE set
          }
        } catch (err) {
          // Continue
        }
        
        // 6. Now delete the cliente itself
        const { error } = await deleteById('clientes', id);
        
        if (error) {
          // Provide more detailed error message
          const errorMessage = error.message || String(error);
          if (errorMessage.includes('foreign key') || errorMessage.includes('violates foreign key')) {
            throw new Error('No se puede eliminar este cliente porque tiene registros relacionados (ventas, facturas, etc.). Por favor, elimine primero esos registros o contacte al administrador.');
          }
          throw error;
        }
        
        logger.debug('✅ Cliente eliminado de Supabase:', id);
        
        // Remove from local state
        const updatedClientes = get().clientes.filter(c => c.id !== id);
        set({ clientes: updatedClientes });
        
        return true;
      },
      (error: any) => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        set({ error: errorMessage });
        logger.error('❌ Error deleting cliente:', error);
      },
      (loading) => set({ isLoading: loading }),
      {
        errorPrefix: 'Error al eliminar cliente'
      }
    );
    
    return result !== null;
  },

  clearError: () => set({ error: null }),

  reset: () => set({
    clientes: [],
    isLoading: false,
    error: null,
    clientesTotalCount: null,
    clientesOffset: 0,
    ventasCacheByEmpresaId: {},
    facturasPendientesCacheByEmpresaId: {}
  }),
}));
